import {
  BookingResult,
  BookingTransactionId,
  StockTargetType, SystemContext,
  TransactionQueuedBooking,
} from '../types';
import { BookingOrchestrator } from '../booking-orchestrator';
import { ModelAdapters } from '../interface/model-adapters';

/**
 * Handle für eine laufende Buchungs-Transaktion. Bündelt die zur Anlage
 * verwendeten Adapter und den System-Kontext und delegiert Commit, Rollback
 * und Discard an den Orchestrator.
 */
export class BookingTransaction {
  /**
   * @param id ID der Transaktion
   * @param adapters Zur Anlage verwendete Model-Adapter
   * @param systemContext System-Kontext zum Zeitpunkt der Anlage
   * @param orchestrator Orchestrator, der die Transaktions-Operationen ausführt
   */
  constructor(
    public readonly id: BookingTransactionId,
    private readonly adapters: ModelAdapters,
    private readonly systemContext: SystemContext,
    private readonly orchestrator: BookingOrchestrator,
  ) {}

  /**
   * Schließt die Transaktion erfolgreich ab und leert ihre Queue.
   *
   * @returns Ergebnis des Commits
   */
  public async commit(): Promise<BookingResult> {
    return this.orchestrator.commitTransaction(this.id, this.adapters);
  }

  /**
   * Rollt alle Buchungen der Transaktion zurück.
   *
   * @returns Ergebnis des Rollbacks
   */
  public async rollback(): Promise<BookingResult> {
    return this.orchestrator.rollbackTransaction(
      this.id,
      this.adapters,
      this.systemContext,
      (qb) => true
    );
  }

  /**
   * Rollt nur die Buchungen der Transaktion zurück, deren Ziel die angegebene
   * Maschine ist.
   *
   * @param machineId ID der Maschine, deren Buchungen zurückgerollt werden
   * @returns Ergebnis des Rollbacks
   */
  public async rollbackForSingleMachine(machineId: number): Promise<BookingResult> {
    return this.orchestrator.rollbackTransaction(
      this.id,
      this.adapters,
      this.systemContext,
      (qb: TransactionQueuedBooking) => {
        return qb.booking.targetType === StockTargetType.MACHINE
          && qb.booking.targetId === machineId;
      },
    );
  }

  /**
   * Verwirft die Transaktion und leert ihre Queue, ohne gegenzubuchen.
   *
   * @returns Ergebnis des Verwerfens
   */
  public async discard(): Promise<BookingResult> {
    return this.orchestrator.discardTransaction(this.id, this.adapters);
  }
}
